import { type JSX, useState } from "react";
import styled from "styled-components";
import { useQuery } from "urql";
import InfoIcon from "../../shared/components/icons/InfoIcon";
import TaskIcon from "../../shared/components/icons/TaskIcon";
import Loader from "../../shared/components/Loader";
import StyledModal from "../../shared/components/StyledModal";
import t from "../../shared/translations";
import type { TaskIcon as TaskIconType } from "../../shared/types";
import { useSelectedIds } from "../components/SelectedIds";
import ShowError from "../components/ShowError";
import { learningPathQuery } from "./query";

interface IScoreboard {
    id: string;
    name: string;
    score: number;
    specifiedTasks: {
        id: string;
    }[];
    specifiedFeedbackTasks: {
        id: string;
    }[];
}

interface IProps {
    scoreboard: IScoreboard;
}

const InfoIconWrapper = styled.div`
    display: inline-block;
    margin-left: 0.5rem;
    cursor: pointer;
    
    svg {
        width: 32px;
        height: 32px;
        vertical-align: middle;
    }
`;

const TaskList = styled.ul`
    margin: 1rem 0;
    padding-left: 0;
    list-style: none;

    li {
        margin-bottom: 0.5rem;
    }
`;

const TaskItem = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;

    svg {
        width: 32px;
        height: 32px;
        flex-shrink: 0;
    }
`;

function ScoreboardInfo({ scoreboard }: IProps): JSX.Element {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const { pathId } = useSelectedIds();

    const [learningPathResult] = useQuery({
        query: learningPathQuery,
        variables: { id: pathId || "" },
        pause: !pathId,
    });

    const openInfo = () => {
        setIsModalOpen(true);
    };

    const closeInfo = () => {
        setIsModalOpen(false);
    };

    const getOrderedTasks = () => {
        if (!learningPathResult.data) {
            return [];
        }

        const specifiedTaskIds = new Set(scoreboard.specifiedTasks.map((t) => t.id));
        const specifiedFeedbackTaskIds = new Set(scoreboard.specifiedFeedbackTasks.map((t) => t.id));
        const orderedTasks: Array<{ id: string; icon: TaskIconType; shortTitle: string }> = [];

        for (const item of learningPathResult.data.learningPath.learningPathItems) {
            if (item.type === "event" || item.type === "submitted-task-only") {
                continue;
            }

            for (const task of item.tasks) {
                if (specifiedTaskIds.has(task.id)) {
                    orderedTasks.push(task);
                }
            }

            if (item.feedbackTask && specifiedFeedbackTaskIds.has(item.feedbackTask.id)) {
                orderedTasks.push(item.feedbackTask);
            }
        }

        return orderedTasks;
    };

    const orderedTasks = getOrderedTasks();

    return (
        <>
            <InfoIconWrapper onClick={openInfo}>
                <InfoIcon />
            </InfoIconWrapper>
            <StyledModal
                isOpen={isModalOpen}
                onRequestClose={closeInfo}
                contentLabel={`Scoreboard Info: ${scoreboard.name}`}
            >
                <h4>{scoreboard.name}</h4>
                <p>{t("player.performance.info-scoreboard-description")}</p>
                {learningPathResult.fetching && <Loader />}
                {learningPathResult.error && <ShowError />}
                {orderedTasks.length > 0 && (
                    <>
                        <h5>{t("player.performance.scoreboard-tasks-heading")}</h5>
                        <TaskList>
                            {orderedTasks.map((task) => (
                                <li key={task.id}>
                                    <TaskItem>
                                        <TaskIcon icon={task.icon} />
                                        <span>{task.shortTitle}</span>
                                    </TaskItem>
                                </li>
                            ))}
                        </TaskList>
                    </>
                )}
            </StyledModal>
        </>
    );
}

export default ScoreboardInfo;
